import {
  extractBoardDocumentSnapshot,
  framesFromEditor,
  shouldPersistBoardSnapshot,
} from './boardSnapshot';
import {
  isArrowLabelOnlyUpdate,
  isFrameNameOnlyUpdate,
} from './boardDocumentChanges';
import { readArrowLabel } from './arrowTerminalLabels';

const ANNOTATION_SHAPE_TYPES = new Set(['frame', 'arrow']);
const ANNOTATION_PERSIST_MS = 300;
export const LABEL_PERSIST_MS = 800;

function isArrowBinding(record) {
  return record?.typeName === 'binding' && record?.type === 'arrow';
}

function isLabelOnlyAnnotationChange(prev, next) {
  return isArrowLabelOnlyUpdate(prev, next) || isFrameNameOnlyUpdate(prev, next);
}

/**
 * True when the change is noise we should not persist (actor meta, index, etc.).
 * Arrow geometry lives in props.start/end/bend — those must NOT be treated as noise.
 */
function isNoiseOnlyAnnotationChange(prev, next) {
  if (
    prev?.x !== next?.x
    || prev?.y !== next?.y
    || prev?.rotation !== next?.rotation
  ) {
    return false;
  }

  if (next?.type === 'arrow') {
    if (prev?.props !== next?.props) return false;
    if (
      readArrowLabel(prev) !== readArrowLabel(next)
      || prev?.meta?.label !== next?.meta?.label
      || prev?.meta?.startLabel !== next?.meta?.startLabel
      || prev?.meta?.endLabel !== next?.meta?.endLabel
    ) {
      return false;
    }
    return true;
  }

  // Frames: persist move/resize/rename; ignore actor-meta-only updates.
  if (
    prev?.props?.w !== next?.props?.w
    || prev?.props?.h !== next?.props?.h
    || prev?.props?.name !== next?.props?.name
  ) {
    return false;
  }
  return true;
}

/**
 * Persist frames, link lines, and arrow bindings immediately — they only live in
 * tldrawSnapshot (unlike pins, which also sync from placements).
 *
 * @returns {() => void} unregister
 */
export function registerBoardAnnotationPersist(editor, { shouldPersist, onPersist } = {}) {
  if (!editor?.sideEffects?.registerAfterCreateHandler) return () => {};

  let changeTimer = null;

  const canPersist = () => {
    if (typeof shouldPersist === 'function') return shouldPersist();
    return true;
  };

  const persistNow = () => {
    if (!canPersist()) return;
    try {
      const snapshot = extractBoardDocumentSnapshot(editor);
      if (!shouldPersistBoardSnapshot(editor, snapshot)) return;
      onPersist?.(snapshot, { frames: framesFromEditor(editor) });
    } catch (err) {
      console.warn('Failed to persist board annotations', err);
    }
  };

  const schedulePersist = (delayMs = ANNOTATION_PERSIST_MS) => {
    if (!canPersist()) return;
    clearTimeout(changeTimer);
    changeTimer = setTimeout(persistNow, delayMs);
  };

  const isPersistableSource = source => source !== 'remote';

  const onShapeCreate = (shape, source) => {
    if (!isPersistableSource(source) || !ANNOTATION_SHAPE_TYPES.has(shape?.type)) return;
    persistNow();
  };

  const onShapeChange = (prev, next, source) => {
    if (!isPersistableSource(source)) return;
    if (!ANNOTATION_SHAPE_TYPES.has(next?.type)) return;
    if (isNoiseOnlyAnnotationChange(prev, next)) return;
    schedulePersist(
      isLabelOnlyAnnotationChange(prev, next) ? LABEL_PERSIST_MS : ANNOTATION_PERSIST_MS
    );
  };

  const onShapeDelete = (shape, source) => {
    if (!isPersistableSource(source) || !ANNOTATION_SHAPE_TYPES.has(shape?.type)) return;
    persistNow();
  };

  const onBindingCreate = (binding, source) => {
    if (!isPersistableSource(source) || !isArrowBinding(binding)) return;
    persistNow();
  };

  const onBindingChange = (prev, next, source) => {
    if (!isPersistableSource(source) || !isArrowBinding(next)) return;
    if (
      prev?.fromId === next?.fromId
      && prev?.toId === next?.toId
      && prev?.props?.terminal === next?.props?.terminal
    ) {
      return;
    }
    schedulePersist();
  };

  const onBindingDelete = (binding, source) => {
    if (!isPersistableSource(source) || !isArrowBinding(binding)) return;
    persistNow();
  };

  const unregisters = [
    editor.sideEffects.registerAfterCreateHandler('shape', onShapeCreate),
    editor.sideEffects.registerAfterChangeHandler('shape', onShapeChange),
    editor.sideEffects.registerAfterDeleteHandler('shape', onShapeDelete),
    editor.sideEffects.registerAfterCreateHandler('binding', onBindingCreate),
    editor.sideEffects.registerAfterChangeHandler('binding', onBindingChange),
    editor.sideEffects.registerAfterDeleteHandler('binding', onBindingDelete),
  ];

  return () => {
    const hadPending = changeTimer != null;
    clearTimeout(changeTimer);
    changeTimer = null;
    // Flush any pending move/resize before unregistering.
    if (hadPending) persistNow();
    for (const unregister of unregisters) unregister?.();
  };
}
